/**
 * Date conversion utilities for handling ISO 8601 date strings and Date objects
 * Used throughout the application to convert between API string responses and TypeScript Date types
 */

export const formatDateTime = (value: unknown): string => {
  const dateString = value ? String(value) : '';
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Converts an ISO 8601 date string to a Date object
 * Returns null if the input is null, undefined, or invalid
 */
export const parseISODate = (
  dateString: string | null | undefined
): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Converts a Date object to an ISO 8601 string
 * Returns null if the input is null or undefined
 */
export const formatDateToISO = (
  date: Date | null | undefined
): string | null => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Converts a Date object to YYYY-MM-DD format for HTML date inputs
 * Returns empty string if the input is null or undefined
 */
export const formatDateForInput = (
  date: Date | string | null | undefined
): string => {
  if (!date) return '';

  // Handle both Date objects and ISO strings
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toISOString().split('T')[0];
};

/**
 * Converts YYYY-MM-DD string from HTML date input to Date object
 * Returns null if the input is empty or invalid
 */
export const parseDateFromInput = (
  dateString: string | null | undefined
): Date | null => {
  if (!dateString || dateString.trim() === '') return null;

  // Handle YYYY-MM-DD format by appending time for consistent parsing
  const fullDateString = dateString.includes('T')
    ? dateString
    : `${dateString}T00:00:00.000Z`;
  return parseISODate(fullDateString);
};

/**
 * Converts API response date fields from strings to Date objects
 * Handles nested objects and arrays
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint must accept plain TS interfaces without index signatures (e.g. CreateManufacturerRequest); Record<string, unknown> rejects those at call sites due to TS's index-signature check
export const convertDateFields = <T extends Record<string, any>>(
  data: T,
  dateFields: (keyof T)[]
): T => {
  if (!data || typeof data !== 'object') return data;

  const converted = { ...data };

  for (const field of dateFields) {
    const value = converted[field];
    if (typeof value === 'string') {
      converted[field] = parseISODate(value) as T[keyof T];
    }
  }

  return converted;
};

/**
 * Converts Date objects back to ISO strings for API requests
 * Handles nested objects and arrays
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint must accept plain TS interfaces without index signatures (e.g. CreateManufacturerRequest); Record<string, unknown> rejects those at call sites due to TS's index-signature check
export const convertDatesToStrings = <T extends Record<string, any>>(
  data: T,
  dateFields?: readonly string[] | (keyof T)[]
): T => {
  if (!data || typeof data !== 'object') return data;

  const converted = { ...data };

  // If no specific fields provided, auto-detect date fields
  const fieldsToCheck =
    dateFields || Object.keys(converted).filter(key => /_date$/.test(key));

  for (const field of fieldsToCheck) {
    const value = converted[field as keyof T];
    if (value && Object.prototype.toString.call(value) === '[object Date]') {
      converted[field as keyof T] = formatDateToISO(
        value as Date
      ) as T[keyof T];
    }
  }

  return converted;
};

/**
 * Date field names for each domain entity
 */
export const DATE_FIELDS = {
  facility: [
    'qta_expiration_date',
    'audit_date',
    'created_date',
    'modified_date',
  ] as const,

  grade: ['created_date', 'modified_date'] as const,

  manufacturer: ['created_date', 'modified_date'] as const,

  product: ['created_date', 'modified_date'] as const,

  therapeuticArea: ['created_date', 'modified_date'] as const,
} as const;

/**
 * Formats a date for display to the user
 * Returns a localized date string or empty string if date is invalid
 */
export const formatDateForDisplay = (
  date: Date | string | null | undefined
): string => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toLocaleDateString();
};

/**
 * Checks if a date is valid
 */
export const isValidDate = (
  date: Date | string | null | undefined
): boolean => {
  if (!date) return false;
  const dateObj = date instanceof Date ? date : new Date(date);
  return !isNaN(dateObj.getTime());
};
